/**
 * Golf courses that are CLOSED — declared ONCE (T608 M8b).
 *
 * Two facts about a closed course have to stay in lockstep, and before this file
 * they could not: the CUSTOMER must not be offered it, and the OPERATOR must be
 * able to see that it is closed and until when. Part A shipped the first as a
 * name list inside core/data/rates-repo.ts, which is a pg-importing server
 * module the client bundle can never touch — so the operator half had nowhere to
 * read the same list from. Hence shared/: the customer-hidden set and the
 * operator-visible marker are now the SAME declaration, and dropping a course
 * off the closure list turns both off together.
 *
 * Why the marker matters, in Helen's words (Alex's ruling, 2026-07-30): when a
 * customer rings asking whether we serve the European Club, she should be able
 * to say "it is closed for a redesign, reopening 2027" — not fail to find it and
 * sound like we do not serve it. An ABSENT course and a merely-unmarked-but-
 * present course look identical to her, so leaving the row in place without
 * saying WHY satisfies the letter of that ruling and fails its purpose.
 *
 * What a closure does NOT do: no rate row, no price, no audit row and no DDL is
 * touched. The customer exclusion sits ABOVE the golf_courses_public view
 * (rates-repo.listGolfCourseNames) and the operator marker is a rendered string
 * — so a course reopening is an edit to this file and nothing else.
 */
import { splitRouteLabel } from './route-label.js';

export type CourseClosure = {
  /**
   * EVERY name the course has been published under, current spelling first.
   *
   * More than one entry is not tidiness. The customer exclusion is a match
   * against the exact bytes `golf_courses_public` returns, and on an
   * already-seeded database the seeder's superseded-key pass cannot delete a
   * stale rate row that carries rate_audit history (the FK is NO ACTION — see
   * seed-pg.ts step 2b), so the view can still publish a RETIRED spelling.
   * Measured: after re-seeding a database that held the pre-M8 keys, the view
   * returned the retained "The European Club". Every published spelling
   * therefore has to be listed, or the closed course stays selectable under an
   * old name on exactly those databases.
   */
  names: readonly string[];
  /** The year it reopens. "Closed" with no "until when" is not what the ruling asked for. */
  reopensYear: number;
  /** Why it closed, in the words Helen can say to a customer. */
  reason: string;
};

/**
 * THE ONE SWITCH. Empty this array and the course is customer-bookable again and
 * the operator marker disappears, in one edit.
 *
 * Brittas Bay Club: closed for a course redesign, reopening 2027. Its 3 hub
 * routes and 12 band prices are kept verbatim so the pricing work survives the
 * closure.
 */
export const CLOSED_COURSES: readonly CourseClosure[] = [
  {
    names: ['Brittas Bay Club', 'The European Club'],
    reopensYear: 2027,
    reason: 'course redesign',
  },
];

/**
 * Every published spelling of every closed course, current spellings first.
 *
 * This is the set the CUSTOMER-facing course list subtracts
 * (rates-repo.COURSES_HIDDEN_FROM_CUSTOMERS is exactly this array), which is why
 * it is derived rather than typed twice.
 */
export const CLOSED_COURSE_NAMES: readonly string[] = CLOSED_COURSES.flatMap((c) => c.names);

/**
 * THE CUSTOMER-FACING SUBTRACTION — the pure decision, extracted so it can be
 * pinned without a database (T608 M8 remediation).
 *
 * Why it is a function and not an inline `.filter` at the one call site: the
 * subtraction is the single control stopping a CLOSED golf course reaching a
 * customer, and inline it was provably untestable. Measured by the M8 security
 * review: deleting the filter from `rates-repo.listGolfCourseNames` left the full
 * container suite at 2407 passed / 100 skipped / 0 failed — byte-identical to the
 * clean baseline. Every assertion that actually CALLED the reader sat in a
 * `DATABASE_URL`-gated arm (`golf_courses_public` needs `split_part`, which pg-mem
 * does not implement, so the view cannot be read without real Postgres), and the
 * always-on arms only pinned this list's CONTENTS — never that anything subtracts
 * it. The gated arm is legitimate and stays; the always-on gap was not.
 *
 * Takes the names the view published and returns what the customer may see. Pure,
 * pg-free, and pinned always-on in test/core/golf-course-names.spec.ts both here
 * and THROUGH the reader (a stub pool), because a pin on this function alone would
 * still stay green if the call site dropped it.
 */
export function hideClosedCourses(names: readonly string[]): string[] {
  return names.filter((name) => !CLOSED_COURSE_NAMES.includes(name));
}

/**
 * The operator-facing closure line for a stored rate label, or null when the
 * label names nothing closed (the overwhelming majority — this is deliberately
 * NOT an unconditional marker).
 *
 * Takes the STORED label so every operator surface can call it with what it
 * already holds. A golf COURSE leg is stored as "<hub> — → <course>", so the
 * course end is recovered through the one splitter (shared/route-label.ts)
 * rather than a local string scan; an AIRPORT leg names no course and returns
 * null even though it splits; any other label (an airport town, a wedding venue)
 * is matched whole, so a closure could name a bare course too.
 */
export function closureNoteFor(label: string | null | undefined): string | null {
  if (typeof label !== 'string') return null;
  const ends = splitRouteLabel(label);
  const course = ends === null ? label.trim() : ends.courseLeg ? ends.destination : null;
  if (course === null || course === '') return null;
  const closure = CLOSED_COURSES.find((c) => c.names.includes(course));
  if (closure === undefined) return null;
  return `Closed for a ${closure.reason}, reopening ${closure.reopensYear}`;
}
